import { useEffect, useState } from "react";
import { z } from "zod";

const versionSchema = z.object({ buildId: z.string().uuid() });
const dismissedSchema = z.array(z.string());
const storageKey = "di.dismissed-build-updates";
const pollInterval = 60_000;
const observationGap = 5_000;
const requestTimeout = 10_000;

type Observation =
  | { kind: "unconfirmed" }
  | { kind: "candidate"; buildId: string; observedAt: number }
  | { kind: "confirmed"; buildId: string };

function observe(
  previous: Observation,
  buildId: string | null,
  now: number,
): Observation {
  if (buildId === null) {
    return previous.kind === "candidate" ? { kind: "unconfirmed" } : previous;
  }
  if (buildId === __BUILD_ID__) return { kind: "unconfirmed" };
  if (previous.kind === "confirmed" && previous.buildId === buildId) {
    return previous;
  }
  if (previous.kind === "candidate" && previous.buildId === buildId) {
    return now - previous.observedAt >= observationGap
      ? { kind: "confirmed", buildId }
      : previous;
  }
  return { kind: "candidate", buildId, observedAt: now };
}

function readDismissed(): string[] {
  try {
    const value: unknown = JSON.parse(
      sessionStorage.getItem(storageKey) ?? "[]",
    );
    return dismissedSchema.parse(value);
  } catch {
    return [];
  }
}

export function useBuildUpdate() {
  const [observation, setObservation] = useState<Observation>({
    kind: "unconfirmed",
  });
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    if (!import.meta.env.PROD || import.meta.env.MODE !== "production") return;

    let disposed = false;
    let activeRequest: {
      controller: AbortController;
      timeout: ReturnType<typeof setTimeout>;
    } | null = null;
    let interval: ReturnType<typeof setInterval> | undefined;
    let lastStarted = -Infinity;

    const record = (buildId: string | null) => {
      if (!disposed) {
        setObservation((previous) => observe(previous, buildId, Date.now()));
      }
    };

    const check = async () => {
      if (
        disposed ||
        document.visibilityState !== "visible" ||
        activeRequest ||
        Date.now() - lastStarted < observationGap
      ) {
        return;
      }
      lastStarted = Date.now();
      const controller = new AbortController();
      const timeout = setTimeout(() => {
        controller.abort();
        if (activeRequest?.controller === controller) {
          activeRequest = null;
          record(null);
        }
      }, requestTimeout);
      activeRequest = { controller, timeout };
      try {
        const response = await fetch("/version.json", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("Build version unavailable");
        const payload: unknown = await response.json();
        const { buildId } = versionSchema.parse(payload);
        if (!controller.signal.aborted) record(buildId);
      } catch {
        if (!controller.signal.aborted) record(null);
      } finally {
        clearTimeout(timeout);
        if (activeRequest?.controller === controller) activeRequest = null;
      }
    };

    const onVisibility = () => {
      clearInterval(interval);
      if (document.visibilityState === "visible") {
        void check();
        interval = setInterval(() => void check(), pollInterval);
      } else {
        if (activeRequest) {
          activeRequest.controller.abort();
          clearTimeout(activeRequest.timeout);
          activeRequest = null;
        }
        record(null);
      }
    };
    const onWake = () => void check();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
    onVisibility();

    return () => {
      disposed = true;
      clearInterval(interval);
      if (activeRequest) {
        activeRequest.controller.abort();
        clearTimeout(activeRequest.timeout);
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
    };
  }, []);

  const buildId = observation.kind === "confirmed" ? observation.buildId : null;
  const dismiss = () => {
    if (buildId === null) return;
    const next = [...new Set([...dismissed, buildId])];
    setDismissed(next);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(next));
    } catch {}
  };

  return {
    updateAvailable: buildId !== null && !dismissed.includes(buildId),
    dismiss,
  };
}
